/**
 * Faceted filtering, attention detection and sorting for the Labs page.
 *
 * Pure functions only — no React, no clock reads. `now` is always passed in so
 * behaviour is deterministic and unit-testable. Every field used here already
 * arrives from GET /api/labs, so filtering never needs a round trip.
 */

/* ---------------------------------------------------------------- attention */

/** Past its due date but not yet closed — students can still work after the deadline. */
export function isPastDue(lab, now) {
  return Boolean(lab.dueDate) && lab.status !== 'closed' && new Date(lab.dueDate) < now;
}

/**
 * Closed, yet containers are still running. Costs node capacity and storage
 * until the pods are reclaimed, and is invisible on the card grid today.
 */
export function isClosedWithRunning(lab) {
  return lab.status === 'closed' && (lab.containerStats?.running || 0) > 0;
}

/** No deadline at all. Currently the majority state, so worth surfacing rather than hiding. */
export function hasNoDeadline(lab) {
  return !lab.dueDate && !lab.closeDate;
}

/**
 * True when nothing will ever close this lab, so its pods and volumes stay
 * allocated until somebody closes it by hand.
 *
 * LabScheduler closes an open lab on `closeDate`, or failing that on
 * `dueDate + gracePeriodMinutes`. So a due date alone is enough — a lab is only
 * stranded when BOTH are missing, or when auto-close has been switched off.
 *
 * That second case is the one worth surfacing: a lab with both dates set and
 * `autoCloseOnDeadline: false` looks completely healthy on the page and will
 * still pin its resources forever. Nothing showed it before.
 *
 * Archived labs are excluded — they are already finished.
 */
export function neverAutoCloses(lab) {
  if (lab.status === 'closed' || lab.status === 'archived') return false;
  if (lab.autoCloseOnDeadline === false) return true;
  return hasNoDeadline(lab);
}

/** How a lab will end, for the badge on its card. */
export function closurePlan(lab) {
  if (lab.status === 'closed' || lab.status === 'archived') return null;
  if (lab.autoCloseOnDeadline === false) return { kind: 'manual', reason: 'auto-close-off' };
  if (lab.closeDate) return { kind: 'scheduled', at: lab.closeDate, source: 'closeDate' };
  // The scheduler's fallback: dueDate + grace becomes the effective close time.
  if (lab.dueDate) {
    const grace = Number(lab.gracePeriodMinutes || 0);
    return {
      kind: 'scheduled',
      at: new Date(new Date(lab.dueDate).getTime() + grace * 60000).toISOString(),
      source: 'dueDate',
      graceMinutes: grace,
    };
  }
  return { kind: 'manual', reason: 'no-dates' };
}

/** Attention flags for a lab, as facet values. Empty array means nothing is wrong. */
export function attentionFlags(lab, now) {
  const flags = [];
  if (isPastDue(lab, now)) flags.push('past-due');
  if (isClosedWithRunning(lab)) flags.push('running-while-closed');
  if (hasNoDeadline(lab)) flags.push('no-deadline');
  if (neverAutoCloses(lab)) flags.push('never-closes');
  return flags;
}

/** True when a lab needs a human to look at it. Drives the default sort and the row stripe. */
export function needsAttention(lab, now) {
  // A lab that will never close is a standing cost, not a transient state, so
  // it belongs in the default sort alongside the other things needing a human.
  return isPastDue(lab, now) || isClosedWithRunning(lab) || neverAutoCloses(lab);
}

/* ------------------------------------------------------------------ helpers */

const uniqueSorted = (values) => [...new Set(values.filter((v) => v !== undefined && v !== null && v !== ''))]
  .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

export const ownerName = (lab) => lab.createdBy?.name || lab.createdBy?.email || 'Unassigned';

/** UDST term code (1268) -> "Fall 26". Mirrors the display used on the cards. */
export function termLabel(code) {
  const s = String(code || '');
  if (s.length !== 4) return s || 'No term';
  const names = { 8: 'Fall', 1: 'Winter', 5: 'Spring' };
  return names[s.charAt(3)] ? `${names[s.charAt(3)]} ${s.substring(1, 3)}` : s;
}

/* ------------------------------------------------------------------- facets */

/**
 * Facet definitions. `options` is derived from the data so the bar only ever
 * offers values that exist; `valuesFor` returns the facet values a lab holds.
 *
 * Semantics: OR within a facet, AND across facets — the convention people
 * already know from issue trackers and shopping filters.
 */
export const FACETS = [
  {
    key: 'status',
    label: 'Status',
    options: (labs) => uniqueSorted(labs.map((l) => l.status))
      .map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1), tone: statusTone(v) })),
    valuesFor: (lab) => [lab.status],
  },
  {
    key: 'attention',
    label: 'Attention',
    options: () => [
      { value: 'past-due', label: 'Past due', tone: 'critical' },
      { value: 'running-while-closed', label: 'Running while closed', tone: 'critical' },
      { value: 'no-deadline', label: 'No deadline set', tone: 'warning' },
      { value: 'never-closes', label: 'Never frees resources', tone: 'critical' },
    ],
    valuesFor: (lab, now) => attentionFlags(lab, now),
  },
  {
    key: 'course',
    label: 'Course',
    options: (labs) => uniqueSorted(labs.map((l) => l.courseCode)).map((v) => ({ value: v, label: v })),
    valuesFor: (lab) => [lab.courseCode],
  },
  {
    key: 'section',
    label: 'Section',
    options: (labs) => uniqueSorted(labs.map((l) => l.section)).map((v) => ({ value: String(v), label: `S${v}` })),
    valuesFor: (lab) => [String(lab.section ?? '')],
  },
  {
    key: 'owner',
    label: 'Owner',
    options: (labs) => uniqueSorted(labs.map(ownerName)).map((v) => ({ value: v, label: v })),
    valuesFor: (lab) => [ownerName(lab)],
  },
  {
    key: 'term',
    label: 'Term',
    options: (labs) => uniqueSorted(labs.map((l) => l.term)).map((v) => ({ value: String(v), label: termLabel(v) })),
    valuesFor: (lab) => [String(lab.term ?? '')],
  },
  {
    key: 'setup',
    label: 'Setup',
    options: () => [
      { value: 'starter-code', label: 'Starter code' },
      { value: 'open-access', label: 'Open access' },
      { value: 'recording', label: 'Recording' },
      { value: 'semester', label: 'Semester' },
    ],
    valuesFor: (lab) => [
      lab.hasStarterCode ? 'starter-code' : null,
      lab.requireD2LAccess ? null : 'open-access',
      lab.screenRecordingEnabled ? 'recording' : null,
      lab.isSemesterLab ? 'semester' : null,
    ].filter(Boolean),
  },
];

function statusTone(status) {
  if (status === 'open') return 'success';
  if (status === 'closed') return 'critical';
  return 'warning';
}

export const FACET_KEYS = FACETS.map((f) => f.key);

/* ------------------------------------------------------------------ matching */

/** Free-text match across the fields a person would actually type. */
export function matchesSearch(lab, search) {
  const q = String(search || '').trim().toLowerCase();
  if (!q) return true;
  return [lab.name, lab.description, lab.courseCode, ownerName(lab), `s${lab.section ?? ''}`]
    .some((field) => String(field || '').toLowerCase().includes(q));
}

/**
 * Does a lab satisfy the active facets?
 * `skipKey` omits one facet — used to compute that facet's own counts, so a
 * pill shows how many results it *would* add rather than how many survive it.
 */
export function matchesFacets(lab, active, now, skipKey) {
  return FACETS.every((facet) => {
    if (facet.key === skipKey) return true;
    const selected = active[facet.key];
    if (!selected || selected.length === 0) return true;
    return facet.valuesFor(lab, now).some((v) => selected.includes(v));
  });
}

export function applyFilters(labs, { active, search, now }) {
  return labs.filter((lab) => matchesSearch(lab, search) && matchesFacets(lab, active, now));
}

/**
 * Count per option for one facet, evaluated against every OTHER active facet.
 * A zero count means selecting it adds nothing — the bar disables it rather
 * than removing it, so the row never reflows under the cursor.
 */
export function facetCounts(labs, facet, { active, search, now }) {
  const pool = labs.filter((lab) => matchesSearch(lab, search) && matchesFacets(lab, active, now, facet.key));
  const counts = {};
  facet.options(labs).forEach((opt) => {
    counts[opt.value] = pool.filter((lab) => facet.valuesFor(lab, now).includes(opt.value)).length;
  });
  return counts;
}

/* ------------------------------------------------------------------ sorting */

export const SORTS = [
  { value: 'attention', label: 'Needs attention first' },
  { value: 'due', label: 'Due date, soonest' },
  { value: 'name', label: 'Name, A–Z' },
  { value: 'students', label: 'Students, most' },
  { value: 'running', label: 'Running, most' },
  { value: 'created', label: 'Recently created' },
];

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true });
const dueValue = (lab) => (lab.dueDate ? new Date(lab.dueDate).getTime() : Number.POSITIVE_INFINITY);

export function sortLabs(labs, sortKey, now) {
  const list = [...labs];
  switch (sortKey) {
    case 'due':
      // Labs with no deadline sort last rather than first.
      return list.sort((a, b) => dueValue(a) - dueValue(b) || byName(a, b));
    case 'name':
      return list.sort(byName);
    case 'students':
      return list.sort((a, b) => (b.studentCount || 0) - (a.studentCount || 0) || byName(a, b));
    case 'running':
      return list.sort((a, b) => (b.containerStats?.running || 0) - (a.containerStats?.running || 0) || byName(a, b));
    case 'created':
      return list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0) || byName(a, b));
    case 'attention':
    default:
      // Anything wrong floats up; past due outranks running-while-closed.
      return list.sort((a, b) => {
        const rank = (l) => (isPastDue(l, now) ? 2 : isClosedWithRunning(l) ? 1 : 0);
        return rank(b) - rank(a) || dueValue(a) - dueValue(b) || byName(a, b);
      });
  }
}

/* --------------------------------------------------------------- url state */

/**
 * The URL is the state, so a filtered view is a link a colleague can open.
 * Shape: /labs?status=open,draft&owner=Nada%20Ibrahim&sort=due&q=infs3102
 */
export function readFiltersFromParams(params) {
  const active = {};
  FACET_KEYS.forEach((key) => {
    const raw = params.get(key);
    active[key] = raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
  });
  const sortParam = params.get('sort');
  const viewParam = params.get('view');
  const groupParam = params.get('group');
  return {
    active,
    search: params.get('q') || '',
    sort: SORTS.some((s) => s.value === sortParam) ? sortParam : 'attention',
    view: ['cards', 'rows', 'table'].includes(viewParam) ? viewParam : null,
    groupBy: GROUP_BY.some((g) => g.value === groupParam) ? groupParam : 'none',
  };
}

export function writeFiltersToParams({ active, search, sort, view, groupBy }) {
  const params = new URLSearchParams();
  FACET_KEYS.forEach((key) => {
    const values = active[key];
    if (values && values.length) params.set(key, values.join(','));
  });
  if (search) params.set('q', search);
  if (sort && sort !== 'attention') params.set('sort', sort);
  if (view && view !== 'cards') params.set('view', view);
  if (groupBy && groupBy !== 'none') params.set('group', groupBy);
  return params;
}

export function countActiveFilters(active) {
  return FACET_KEYS.reduce((n, key) => n + (active[key]?.length || 0), 0);
}

/** Toggle one value within a facet, returning a new active map. */
export function toggleFacetValue(active, key, value) {
  const current = active[key] || [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return { ...active, [key]: next };
}

export function clearFilters() {
  const active = {};
  FACET_KEYS.forEach((key) => { active[key] = []; });
  return active;
}

/* ------------------------------------------------------------------ grouping */

export const GROUP_BY = [
  { value: 'none', label: 'No grouping' },
  { value: 'course', label: 'Course' },
  { value: 'section', label: 'Section' },
  { value: 'owner', label: 'Owner' },
  { value: 'status', label: 'Status' },
  { value: 'term', label: 'Term' },
];

const groupKeyFor = {
  course: (lab) => lab.courseCode || 'No course',
  section: (lab) => (lab.section ? `Section ${lab.section}` : 'No section'),
  owner: (lab) => ownerName(lab),
  status: (lab) => (lab.status || 'unknown').charAt(0).toUpperCase() + (lab.status || 'unknown').slice(1),
  term: (lab) => termLabel(lab.term),
};

/**
 * Split an already-filtered, already-sorted list into labelled groups.
 * Returns a single unlabelled group when grouping is off, so the caller
 * renders one code path either way.
 */
export function groupLabs(labs, groupBy) {
  if (!groupBy || groupBy === 'none' || !groupKeyFor[groupBy]) {
    return [{ key: '__all__', label: null, labs }];
  }
  const buckets = new Map();
  labs.forEach((lab) => {
    const key = groupKeyFor[groupBy](lab);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(lab);
  });
  return [...buckets.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }))
    .map(([key, items]) => ({ key, label: key, labs: items }));
}

/* -------------------------------------------------------------- saved views */

/**
 * One-click starting points. Each is just a filter state, so a preset and a
 * hand-built filter are the same thing — and both are shareable URLs.
 * `ownerName` is the signed-in user, used by the "Mine" preset.
 */
export function savedViews(currentOwnerName) {
  const base = () => clearFilters();
  const views = [
    {
      id: 'attention',
      label: 'Needs attention',
      tone: 'critical',
      state: { active: { ...base(), attention: ['past-due', 'running-while-closed'] }, sort: 'attention' },
    },
    {
      id: 'open',
      label: 'Open now',
      tone: 'success',
      state: { active: { ...base(), status: ['open'] }, sort: 'due' },
    },
    {
      id: 'no-deadline',
      label: 'No deadline',
      tone: 'warning',
      state: { active: { ...base(), attention: ['no-deadline'] }, sort: 'name' },
    },
    {
      id: 'drafts',
      label: 'Drafts',
      state: { active: { ...base(), status: ['draft'] }, sort: 'created' },
    },
  ];
  if (currentOwnerName) {
    views.unshift({
      id: 'mine',
      label: 'My labs',
      state: { active: { ...base(), owner: [currentOwnerName] }, sort: 'attention' },
    });
  }
  return views;
}

/** Is a saved view exactly what is currently applied? Used to light up its chip. */
export function isViewActive(view, active, sort) {
  const sameFacets = FACET_KEYS.every((key) => {
    const a = [...(active[key] || [])].sort();
    const b = [...(view.state.active[key] || [])].sort();
    return a.length === b.length && a.every((v, i) => v === b[i]);
  });
  return sameFacets && sort === view.state.sort;
}

/* ------------------------------------------------------------ deadline form */

/**
 * Bucket a deadline so urgency can be encoded visually rather than read off a
 * date string. Returns null when the lab has no deadline at all.
 */
export function deadlineUrgency(lab, now) {
  const raw = lab.dueDate || lab.closeDate;
  if (!raw) return null;
  const when = new Date(raw);
  const hours = (when - now) / 3600000;
  if (lab.status === 'closed') return { key: 'closed', label: 'Closed', tone: 'muted' };
  if (hours < 0) return { key: 'overdue', label: 'Overdue', tone: 'critical' };
  if (hours < 24) return { key: 'today', label: 'Due today', tone: 'critical' };
  if (hours < 24 * 7) return { key: 'week', label: 'Due this week', tone: 'warning' };
  return { key: 'later', label: 'Scheduled', tone: 'muted' };
}
